import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';

// --- CONTEXT PROVIDERS ---
import { ThemeProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AccessibilityProvider } from './context/AccessibilityContext';

// --- LAYOUTS & HELPERS ---
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RecipientDashboardLayout from './components/dashboard/RecipientDashboardLayout';
import OrganizationDashboardLayout from './components/dashboard/OrganizationDashboardLayout';
import AdminDashboardLayout from './components/dashboard/AdminDashboardLayout';
import SoundManager from './components/utils/SoundManager';
import PageTracker from './components/PageTracker'; 
import OrganizationDonationModal from './components/donation/OrganizationDonationModal';
// --- PUBLIC PAGES ---
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import AllCampaignsPage from './pages/AllCampaignsPage';
import CampaignProfilePage from './pages/CampaignProfilePage';
import AuthPage from './pages/AuthPage';
import SearchPage from './pages/SearchPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import HowItWorksPage from './pages/HowItWorksPage';
import HelpPage from 'pages/HelpPage';
import SupportedStatesPage from './pages/SupportedStatesPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Organizations from './pages/Organizations';

// --- RECIPIENT DASHBOARD PAGES ---
import OnboardingPage from 'pages/OnboardingPage';
import ProductCatalogPage from './pages/recipient/ProductCatalogPage';
import RecipientDashboardPage from './pages/recipient/RecipientDashboardPage';
import CampaignStatusPage from './pages/recipient/CampaignStatusPage';
import RecipientProfilePage from './pages/recipient/ProfilePage';
import RecipientSettingsPage from './pages/recipient/SettingsPage';
import RecipientHelpPage from './pages/recipient/HelpPage';
import ReuploadDocumentsPage from './pages/ReuploadDocumentsPage';

// --- ORGANIZATION DASHBOARD PAGES ---

import OrganizationDashboardPage from './pages/organization/OrganizationDashboardPage';
import OrganizationStatusPage from './pages/organization/OrganizationStatusPage';
import OrganizationProfilePage from './pages/organization/OrganizationProfilePage';
import OrganizationHelpPage from './pages/organization/OrganizationHelpPage';
import OrganizationSettingsPage from './pages/organization/OrganizationSettingsPage';
import OrganizationCatalogPage from './pages/organization/OrganizationCatalogPage';

// --- DONOR PAGES (PUBLICLY ACCESSIBLE LAYOUT) ---
import DonorAccountPage from './pages/donor/DonorAccountPage';
import DonorGiftsPage from './pages/donor/DonorGiftsPage';

// --- ADMIN DASHBOARD PAGES ---
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManageProductsPage from './pages/admin/ManageProductsPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import ManageAllCampaignsPage from './pages/admin/ManageAllCampaignsPage';
import RecipientHistoryPage from './pages/admin/RecipientHistoryPage';
import ManageSuccessStoriesPage from './pages/admin/ManageSuccessStoriesPage';
import ManageStatsPage from './pages/admin/ManageStatsPage';
import OrganizationCampaignManagement from 'pages/admin/OrganizationCampaignManagement';
import OrganizationCampaignsAdmin from 'pages/admin/OrganizationCampaignsAdmin';
import OrganizationProductsAdmin from 'pages/admin/OrganizationProductsAdmin';
import PurchasedItemsPage from 'pages/admin/PurchasedItems';
import ConsentAnalyticsPage from './pages/admin/ConsentAnalyticsPage';

// Layout for all regular PUBLIC pages
const PublicLayout = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      <Header />
      <main className="flex-grow pt-32"><Outlet /></main>
      <Footer />
    </div>
  );
};

// Main App component that controls all routing
export default function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!googleClientId) {
    return <div>Configuration error: Google Client ID is missing.</div>;
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <ThemeProvider>
          <AccessibilityProvider>
            <CartProvider>
              <AuthProvider>
                <SoundManager />
                 <PageTracker />
                <Routes>
                  {/* --- PUBLIC ROUTES (AND DONOR ACCOUNT PAGES) --- */}
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/help" element={<HelpPage />} />
                    <Route path="/supported-states" element={<SupportedStatesPage />} />
                    <Route path="campaigns" element={<AllCampaignsPage />} />
                    <Route path="campaigns/:id" element={<CampaignProfilePage />} />
                    <Route path="search" element={<SearchPage />} />
                    <Route path="cart" element={<CartPage />} />
                    <Route path="checkout" element={<CheckoutPage />} />
                    <Route path="/donor/account" element={<DonorAccountPage />} />
                    <Route path="/donor/gifts" element={<DonorGiftsPage />} />
                    <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                    <Route path="/organizations" element={<Organizations />} />
                    <Route path="/donate/:campaignId" element={<OrganizationDonationModal />} />
                  </Route>

                  {/* --- STANDALONE AUTH PAGE --- */}
                  <Route path="/login" element={<AuthPage />} />

                  {/* --- PROTECTED ROUTES (DASHBOARDS) --- */}
                  <Route element={<ProtectedRoute />}>
                    {/* RECIPIENT DASHBOARD */}
                    <Route path="/recipient" element={<RecipientDashboardLayout />}>
                      <Route index element={<RecipientDashboardPage />} />
                      <Route path="dashboard" element={<RecipientDashboardPage />} />
                      <Route path="status" element={<CampaignStatusPage />} />
                      <Route path="profile" element={<RecipientProfilePage />} />
                      <Route path="settings" element={<RecipientSettingsPage />} />
                      <Route path="help" element={<RecipientHelpPage />} />
                      <Route path="catalog" element={<ProductCatalogPage />} />
                      <Route path="/recipient/reupload-documents" element={<ReuploadDocumentsPage />} />
                    </Route>
                     <Route path="onboarding" element={<OnboardingPage />} />
                  </Route>
                  <Route element={<ProtectedRoute />}>
                    <Route path="/organization" element={<OrganizationDashboardLayout />}>
                      <Route index element={<OrganizationDashboardPage />} />
                      <Route path="onboarding" element={<OnboardingPage />} />
                      <Route path="dashboard" element={<OrganizationDashboardPage />} />
                      <Route path="status" element={<OrganizationStatusPage />} />
                      <Route path="profile" element={<OrganizationProfilePage />} />
                      <Route path="help" element={<OrganizationHelpPage />} />
                      <Route path="settings" element={<OrganizationSettingsPage />} />
                      <Route path="catalog" element={<OrganizationCatalogPage />} />
                    </Route>
                  </Route>
                  {/* ADMIN DASHBOARD */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/admin" element={<AdminDashboardLayout />}>
                      <Route path="dashboard" element={<AdminDashboardPage />} />
                      <Route path="campaigns" element={<ManageAllCampaignsPage />} />
                      <Route path="products" element={<ManageProductsPage />} />
                      <Route path="users" element={<ManageUsersPage />} />
                      <Route path="users/:userId/history" element={<RecipientHistoryPage />} />
                      <Route path="success-stories" element={<ManageSuccessStoriesPage />} />
                      <Route path="stats" element={<ManageStatsPage />} />
                      <Route path="organizations" element={<OrganizationCampaignManagement />} />
                      <Route path="organizations/:orgId/campaigns" element={<OrganizationCampaignsAdmin />} />
                      <Route path='organizations/:orgId/products' element={<OrganizationProductsAdmin />} />
                      <Route path="purchased-items" element={<PurchasedItemsPage />} />
                      <Route path="consent-analytics" element={<ConsentAnalyticsPage />} />
                    </Route>
                  </Route>
                </Routes>
              </AuthProvider>
            </CartProvider>
          </AccessibilityProvider>
        </ThemeProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}